import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Menu, 
  LogOut, 
  ShoppingBag, 
  Wallet,
  BookOpen,
  Package,
  FileText,
  QrCode
} from 'lucide-react';
import BalanceReport from './BalanceReport';
import Ledger from './ledger';
import ItemWiseSale from './itemWiseSale';
import BillSummary, { BillModal } from './BillSummary';
import ShopVisitCode from './ShopVisitCode';
import { CallAPIPromise } from '../routeBilling/menu/dashboard/biiling/modal/comman';
import { useSelector } from 'react-redux';
import { selectPrefix, selectShopDetails } from '../utility/Redux/profile';
import moment from 'moment';



export default function Dashboard({ onLogout }) {
  const [currentPath, setCurrentPath] = useState(window.location.hash.slice(1) || '/dashboard/balance');
 
  useEffect(() => {
    const handleHashChange = () => {
      setCurrentPath(window.location.hash.slice(1) || '/dashboard/balance');
    };
    
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Extract current tab from URL
  const currentTab = currentPath.split('/')[2] || 'balance';

  const navigation = [
    { id: 'balance', label: 'Balance', icon: Wallet, path: '/dashboard/balance' },
    { id: 'ledger', label: 'Ledger', icon: BookOpen, path: '/dashboard/ledger' },
    // { id: 'items', label: 'Item Sales', icon: Package, path: '/dashboard/items' },
    { id: 'bills', label: 'Bills', icon: FileText, path: '/dashboard/bills' },
    { id: 'visit', label: 'Visit Code', icon: QrCode, path: '/dashboard/visit' },
  ];

  const handleNavigation = (path) => {
    window.location.hash = path;
  };
 
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <Sheet>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-0">
                <div className="p-6 border-b">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                      <ShoppingBag className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-gray-900">My Shop</p>
                      <p className="text-gray-500">Shopkeeper</p>
                    </div>
                  </div>
                </div>
                <nav className="p-4 space-y-2">
                  {navigation.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleNavigation(item.path)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                          currentTab === item.id
                            ? 'bg-blue-50 text-blue-600'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        {item.label}
                      </button>
                    );
                  })}
                </nav>
              </SheetContent>
            </Sheet>
            
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <ShoppingBag className="w-4 h-4 text-white" />
              </div>
              <span className="text-gray-900 hidden sm:inline">ShopKeeper Pro</span>
            </div>
          </div>

          <Button 
            variant="ghost" 
            onClick={onLogout}
            className="flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar - Desktop */}
        <aside className="hidden lg:block w-64 bg-white border-r border-gray-200 min-h-[calc(100vh-73px)] sticky top-[73px]">
          <nav className="p-4 space-y-2">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavigation(item.path)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    currentTab === item.id
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-6">
          {/* Mobile Tabs */}
          <div className="lg:hidden mb-4">
            <Tabs value={currentTab} onValueChange={(value) => handleNavigation(`/dashboard/${value}`)} className="w-full">
              <TabsList className="w-full grid grid-cols-4 h-auto">
                {navigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <TabsTrigger 
                      key={item.id} 
                      value={item.id}
                      className="flex flex-col gap-1 py-2 px-1 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600"
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-xs">{item.label.split(' ')[0]}</span>
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </Tabs>
          </div>

          {/* Content Area */}
          <div className="max-w-7xl mx-auto">
            {currentTab === 'balance' && <BalanceReport goToLedger={() => {
              handleNavigation(`/dashboard/${'ledger'}`)
            }} />}
            {currentTab === 'ledger' && <Ledger />}
            {currentTab === 'items' && <ItemWiseSale />}
            {currentTab === 'bills' && <BillSummary />}
            {currentTab === 'visit' && <ShopVisitCode />}
          </div>
        </main>
      </div>
    </div>
  );
}
